import { useCallback, useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { BookOpen, CalendarDays, PencilLine } from "lucide-react";
import { toast } from "@/lib/toast";
import { haptic } from "@/lib/telegram";
import { cn } from "@/lib/utils";
import { useOverlayOpen } from "@/lib/overlay-store";

const MODES = [
  { key: "revision", to: "/revisions", label: "Revision", icon: BookOpen, toast: "Revision Mode Activated" },
  { key: "practice", to: "/questions", label: "Practice", icon: PencilLine, toast: "Practice Mode Activated" },
  { key: "test", to: "/tests", label: "Test", icon: CalendarDays, toast: "Test Mode Activated" },
] as const;

const MODE_KEY = "stp_fab_mode";

/** Routes whose primary call-to-action must never be covered by the pill. */
const HIDDEN_PREFIXES = ["/plans", "/payment", "/admin"];

export function FloatingModeButton() {
  const navigate = useNavigate();
  const overlayOpen = useOverlayOpen();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [modeIndex, setModeIndex] = useState(0);

  useEffect(() => {
    const storedMode = Number(localStorage.getItem(MODE_KEY));
    if (Number.isInteger(storedMode) && storedMode >= 0 && storedMode < MODES.length) {
      setModeIndex(storedMode);
    }
  }, []);

  const activate = useCallback(
    (index: number) => {
      const mode = MODES[index];
      haptic("light");
      localStorage.setItem(MODE_KEY, String(index));
      toast(mode.toast);
      navigate({ to: mode.to });
    },
    [navigate],
  );

  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const mode = MODES[modeIndex];
  const Icon = mode.icon;

  return (
    <div
      className={cn(
        "pointer-events-none fixed right-4 z-40 flex justify-end transition-opacity duration-300",
        overlayOpen && "opacity-0",
      )}
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 6.25rem)" }}
    >
      <motion.button
        type="button"
        whileTap={{ scale: 0.94 }}
        aria-label={`${mode.label} mode — tap to switch study section`}
        onClick={() => {
          const next = (modeIndex + 1) % MODES.length;
          setModeIndex(next);
          activate(next);
        }}
        className={cn(
          "pulse-glow pointer-events-auto flex h-12 min-h-[44px] items-center justify-center gap-2 rounded-[22px] border border-hairline px-4 [background-image:var(--gradient-brand)] shadow-[0_18px_40px_-16px_rgba(99,102,241,0.9)] backdrop-blur-xl",
          overlayOpen && "pointer-events-none",
        )}
      >
        <motion.span
          key={mode.key}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.27, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-2"
        >
          <Icon className="size-5 shrink-0 text-primary-foreground" />
          <span className="whitespace-nowrap text-[13px] font-bold tracking-tight text-primary-foreground">
            {mode.label}
          </span>
        </motion.span>
      </motion.button>
    </div>
  );
}
